import { useCallback, useEffect, useRef, useState } from "react";

/** The browser's speech recognition (Chrome, Edge, Safari). Not in Firefox: the phone keyboard's dictation still works there. */
type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
};

const Ctor = (): (new () => Recognition) | null => {
  const w = window as unknown as { SpeechRecognition?: new () => Recognition; webkitSpeechRecognition?: new () => Recognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
};

/**
 * Talk instead of typing: `onText` gets what's been heard so far (it fills the message box live),
 * and `onDone` the whole thing when the person stops talking. Nothing is recorded or sent to the server.
 */
export function useSpeech(onText: (text: string) => void, onDone: (text: string) => void) {
  const supported = typeof window !== "undefined" && !!Ctor();
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rec = useRef<Recognition | null>(null);
  const heard = useRef("");
  const cb = useRef({ onText, onDone });
  cb.current = { onText, onDone };

  useEffect(() => () => rec.current?.abort(), []);

  const start = useCallback(() => {
    const C = Ctor();
    if (!C || rec.current) return;
    const r = new C();
    r.lang = "en-US";
    // Stops when they pause, like a phone assistant.
    r.continuous = false;
    r.interimResults = true;
    heard.current = "";
    let final = "";
    r.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        if (res.isFinal) final += res[0]!.transcript;
        else interim += res[0]!.transcript;
      }
      heard.current = `${final}${interim}`.trim();
      cb.current.onText(heard.current);
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") setError("The microphone is blocked. Allow it in the browser's site settings.");
      else if (e.error !== "no-speech" && e.error !== "aborted") setError("Couldn't hear that. Try again, or type.");
    };
    r.onend = () => {
      rec.current = null;
      setListening(false);
      if (heard.current) cb.current.onDone(heard.current);
    };
    setError(null);
    rec.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      rec.current = null;
    }
  }, []);

  const stop = useCallback(() => rec.current?.stop(), []);

  return { supported, listening, error, start, stop };
}
